import { ChangeDetectorRef, Component, DestroyRef, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { combineLatestWith, firstValueFrom, Observable } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { LoadingService } from '@core/services/loading.service';
import { FirebaseAnalyticsService } from '@core/services/firebase-analytics.service';
import packageInfo from '../../package.json';
import { GlobalInformationModalService } from '@shared/components/global-information-modal/global-information-modal.service';
import { selectUser } from '@core/modules/user/features/user.slice';
import { UserRole } from '@core/models';
import { CheckPolicyService } from '@modules/private/admin/popups-manager/pages/policy-popup/services/check-policy.service';
import { LOGIN } from '@core/constants/RoutingsPath';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { User } from '@core/modules/user/models';
import { PromoBannersService } from '@shared/components/promo-banners/services/promo-banners.service';
import { ToastPositionType } from 'primeng/toast';
import { isMobile } from '@shared/utils/platform.utils';
import { PushService } from '@core/services/push/push.service';
import { DeepLinkService } from '@core/services/deep-link/deep-link.service';
import { InAppBrowserService } from '@core/services/in-app-browser/in-app-browser.service';

console.log(
  `%c MY INDIBA UI version: ${packageInfo.version} `,
  'color: red; background: yellow; font-size: 20px'
);

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit {
  isLoading$!: Observable<boolean>;
  isLoading!: boolean;

  notificationPosition: ToastPositionType = isMobile() ? 'bottom-center' : 'bottom-right';

  public constructor(
    private readonly destroyRef: DestroyRef,
    private readonly loadingService: LoadingService,
    private readonly router: Router,
    private readonly firebaseAnalyticsService: FirebaseAnalyticsService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly store: Store,
    private readonly globalInformationModalService: GlobalInformationModalService,
    private readonly checkPolicyService: CheckPolicyService,
    private readonly promoBannersService: PromoBannersService,
    private readonly pushService: PushService,
    private readonly deepLinkService: DeepLinkService,
    private readonly inAppBrowserService: InAppBrowserService
  ) {}

  ngOnInit(): void {
    // Registered at boot so a notification tap that cold starts the app is
    // handled; the permission itself is only requested after login (user effects).
    this.pushService.setupListeners();
    // Registered at boot too: a link tapped with the app closed arrives as the
    // launch url before anything else runs.
    this.deepLinkService.setupListeners();
    // One listener for the whole app: an external link opens on top of MyINDIBA
    // instead of sending the user to the system browser.
    this.inAppBrowserService.setupListeners();
    this.loadingService.isLoading
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isLoading) => {
        this.isLoading = isLoading;
        this.changeDetectorRef.detectChanges();
      });
    this.navigationEnd();
  }

  private navigationEnd(): void {
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event) => event instanceof NavigationEnd),
        combineLatestWith(this.store.select(selectUser)),
        tap(async ([event, user]) => {
          this.firebaseAnalyticsService.pageEntered(event as NavigationEnd);

          if (user?.roleId === UserRole.CLIENT && this.router.url !== `/${LOGIN}`) {
            await this.checkAndOpenPolicy(user);
            await this.showPromoBanners();
          }
        })
      )
      .subscribe();
  }

  private async checkAndOpenPolicy(user: User) {
    const response = await firstValueFrom(this.checkPolicyService.checkHasNewPolicy(user.id));

    if (response?.isAccepted === false) {
      this.globalInformationModalService.open(response.translation);
    }
  }

  private async showPromoBanners() {
    this.promoBannersService.clearAll();
    await this.promoBannersService.show();
  }
}
